import React from 'react';
import { omit } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { Router } from 'next/router';
import URI from 'urijs';
import { useAppContext } from 'Context/ReactAppContext';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import DynamicLinkUtil from './DynamicLinkUtil';
export type Props = Readonly<{
  children?: React.ReactNode;
  className?: string;
  'data-pagename'?: string;
  'data-selector'?: string;
  'data-testid'?: string;
  dataTestId?: string;
  isExternalLink?: boolean;
  onClick?: (arg0: React.SyntheticEvent) => any;
  rel?: string;
  target?: string;
  tagname?: string;
  url?: string | null;
  propagateHashParams?: boolean;
  queryParamsToIgnore?: ReadonlyArray<string>;
  shallow?: boolean;
  replace?: boolean;
}>;
const NON_DOM_ATTRIBUTES = [
  'children',
  'dataTestId',
  'isExternalLink',
  'onClick',
  'propagateHashParams',
  'queryParamsToIgnore',
  'replace',
  'shallow',
  'tagname',
  'url',
];
const defaultProps = {
  'data-selector': 'DynamicLink',
};
type ClickHandlerArguments = {
  dynamicLink: DynamicLinkUtil;
  props: Props;
  router: Router;
};

const getClickBehaviour = ({ dynamicLink, props: { onClick }, router }: ClickHandlerArguments) => {
  const hasHash = dynamicLink.hashToSamePage();

  if (!onClick && !hasHash) {
    return null;
  }

  return function handleOnClick(event) {
    if (onClick) {
      onClick(event);
    }

    if (hasHash) {
      event?.preventDefault();
      const { basePath, asPath } = router;
      const newUrl = new URI(dynamicLink.nextUrl).query(new URI(asPath).query(true));
      const newUrlWithHash = `${new URI(basePath).pathname()}${newUrl.toString()}`;
      router.replace(newUrlWithHash.toString(), undefined, {
        shallow: true,
      });
    }
  };
};

const cleanupQueryParams = (queryString: string, queryParams: ReadonlyArray<string> = []) => {
  const completeQuery = new URI(queryString);
  completeQuery.removeQuery(queryParams);
  return completeQuery.readable();
};

const getDynamicProps = ({ dynamicLink, props, router }: ClickHandlerArguments) => {
  return {
    ...omit(props, NON_DOM_ATTRIBUTES),
    onClick: getClickBehaviour({
      dynamicLink,
      props,
      router,
    }),
  };
};

const DynamicLink = ({
  url = '#',
  children,
  propagateHashParams = false,
  queryParamsToIgnore = [],
  tagname: TagName = 'span',
  isExternalLink = false,
  shallow = false,
  replace = false,
  ...props
}: Props) => {
  const appContext = useAppContext();
  const disableSpaRouting = isFeatureFlipped(appContext)('disableSpaRouting');
  const router = useRouter();

  if (url === null) {
    return null;
  }

  const currentUri = new URI(router.asPath);
  const options = {
    propagateHashParams,
  };
  const dynamicLink = new DynamicLinkUtil(currentUri, url, options);
  const target = dynamicLink.isExternalUrl() ? '_blank' : props.target;
  const rel = target === '_blank' ? 'noopener noreferrer' : null;
  const href = cleanupQueryParams(dynamicLink.toString(), queryParamsToIgnore);
  const dynamicProps = getDynamicProps({
    dynamicLink,
    props,
    router: router as Router,
  });

  if (url === '' || href === '') {
    // There are some places in the cms (at least in the mocks) where there are link with empty
    // href, which doesn't work with next. So we render a dummy TagName (span by default) instead
    return <TagName {...omit(props, NON_DOM_ATTRIBUTES)}>{children}</TagName>;
  }

  if (href.startsWith('#')) {
    return (
      <a {...dynamicProps} href={href}>
        {children}
      </a>
    );
  }

  if (
    isExternalLink ||
    target === '_blank' ||
    (disableSpaRouting && !dynamicLink.isOpenLayerLink())
  ) {
    return (
      <a {...dynamicProps} href={href} target={target} rel={rel}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} shallow={shallow} replace={replace} {...dynamicProps}>
      {children}
    </Link>
  );
};

DynamicLink.defaultProps = defaultProps; // eslint-disable-next-line import/no-default-export

export default DynamicLink;
